import { computed, defineComponent, ref, nextTick, onMounted, watch } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Search as VanSearch,
  Popover as VanPopover,
  List as VanList,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import type { PictureTypeItem } from '@/api/types.gen'
import { renderGalleryColorMenu, splitWaterfall } from '@/components/gallery-waterfall'
import {
  getApiV1TukuPictureSeriesByRegion,
  getApiV1TukuPictureSeriesByRegionSearch,
} from '@/api/sdk.gen'

// ==================== 常量 ====================

const PAGE_SIZE = 10

type ColorValue = 1 | 2
const COLOR_MAP: Record<ColorValue, string> = { 1: '彩色', 2: '黑白' }

// ==================== 图库分类页（默认导出） ====================

export default defineComponent({
  name: 'GalleryCategoryPage',
  setup() {
    const router = useRouter()
    const goBack = useSmartBack()
    const route = useRoute()
    const regionStore = useRegionStore()

    // 同步读取 region，确保在任何数据请求之前设置正确的彩种
    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    const seriesId = Number(route.params.id)
    const seriesName = ref(`分类 ${seriesId}`)

    // SEO：图库分类页，标题随分类名动态变化（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `${seriesName.value}_六合图库高清图纸 - ${brand.value}`,
      description: () =>
        `${seriesName.value}图库，汇集各年份高清图纸与资料图片，支持彩色/黑白分类查看，更新及时。`,
      keywords: () => `${seriesName.value},六合图库,图纸,资料图片,${brand.value}`,
    })

    const value = ref('')
    const color = ref<ColorValue>(1)
    const showColor = ref(false)

    const displayList = ref<PictureTypeItem[]>([])
    const pageNum = ref(0)
    const loading = ref(false)
    const finished = ref(false)
    const pictureIds = ref<Set<number>>(new Set())

    async function getNewListPicture() {
      if (finished.value) return
      loading.value = true
      pageNum.value += 1
      try {
        const res = await getApiV1TukuPictureSeriesByRegionSearch({
          path: { region: regionStore.region },
          query: {
            seriesId,
            pageNum: pageNum.value,
            limit: PAGE_SIZE,
            color: String(color.value),
            keyword: value.value.trim() || undefined,
          },
        })
        const list = res.data?.data?.list ?? []

        const newItems = list.filter((item) => {
          if (!item.pictureId) return false
          if (pictureIds.value.has(item.pictureId)) return false
          pictureIds.value.add(item.pictureId)
          return true
        })

        displayList.value = [...displayList.value, ...newItems]
        if (!list.length || list.length < PAGE_SIZE) {
          finished.value = true
        }
      } finally {
        loading.value = false
      }

      await nextTick()
    }

    function resetAndLoad() {
      displayList.value = []
      pictureIds.value = new Set()
      pageNum.value = 0
      loading.value = false
      finished.value = false
      getNewListPicture()
    }

    function onSearch() {
      resetAndLoad()
    }

    function onSelectColor(c: ColorValue) {
      color.value = c
      showColor.value = false
      resetAndLoad()
    }

    async function fetchSeriesName() {
      const res = await getApiV1TukuPictureSeriesByRegion({
        path: { region: regionStore.region },
      })
      const found = (res.data?.data ?? []).find((s) => s.id === seriesId)
      if (found?.name) seriesName.value = found.name
    }

    onMounted(() => {
      fetchSeriesName()
      debugger
      getNewListPicture()
    })

    // 双列瀑布流（奇偶分列）
    const columns = computed(() => splitWaterfall(displayList.value))

    watch(
      () => regionStore.region,
      () => {
        router.replace({ query: { ...route.query, region: regionStore.region } })
        fetchSeriesName()
        resetAndLoad()
      },
    )

    /** 色彩下拉菜单：彩色 / 黑白 */
    const renderColorMenu = () => renderGalleryColorMenu(color.value, onSelectColor)

    /** 瀑布流单张图 */
    const renderImageItem = (item: PictureTypeItem, i: number) => (
      <RouterLink
        key={`${item.pictureId}-${i}`}
        class="image-item btn-reset block"
        to={`/gallery/${item.pictureTypeId}?region=${regionStore.region}`}
      >
        <div class="image-item-image">
          {item.pictureUrl && (
            <img
              class="min-h-[200px]"
              src={item.pictureUrl}
              alt={item.pictureName}
              loading="lazy"
              referrerpolicy="no-referrer"
            />
          )}
        </div>
        <div class="image-item-title">{item.pictureName}</div>
      </RouterLink>
    )

    return () => (
      <div class="tuku-home">
        <VanSticky>
          <div class="sticky-body">
            <VanNavBar title={seriesName.value} onClickLeft={() => goBack()}>
              {{
                left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
              }}
            </VanNavBar>
            <div class="search-hd">
              <VanSearch
                v-model={value.value}
                placeholder="搜索"
                showAction
                shape="round"
                background="#fff"
                onSearch={onSearch}
              >
                {{
                  'left-icon': () => (
                    <VanPopover
                      v-model:show={showColor.value}
                      placement="bottom-start"
                      offset={[0, 5]}
                    >
                      {{
                        reference: () => (
                          <div class="left-icon">&nbsp;{COLOR_MAP[color.value]}&nbsp; ▾</div>
                        ),
                        default: renderColorMenu,
                      }}
                    </VanPopover>
                  ),
                  action: () => (
                    <button type="button" class="search-btn btn-reset block" onClick={onSearch}>
                      搜索
                    </button>
                  ),
                }}
              </VanSearch>
            </div>
          </div>
        </VanSticky>

        <VanList
          loading={loading.value}
          onUpdate:loading={(v: boolean) => (loading.value = v)}
          finished={finished.value}
          finishedText="没有更多了"
          immediateCheck={false}
          onLoad={getNewListPicture}
        >
          <div class="body-image-bd">
            <div class="waterfall van-row">
              {columns.value.map((col) => (
                <div key={col.key} class="van-col van-col--12">
                  {col.items.map((item, i) => renderImageItem(item, i))}
                </div>
              ))}
            </div>
          </div>
        </VanList>
      </div>
    )
  },
})
